import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock3, Compass, Crown, GraduationCap, Library, Map, Sparkles, Star } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const AcademiaPublic = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/api/academy/public/courses")
      .then(({ data }) => { if (active) setCourses(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setCourses([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const featured = useMemo(() => courses.find((course) => course.featured) || courses[0], [courses]);
  const rest = useMemo(() => featured ? courses.filter((course) => course.slug !== featured.slug) : courses, [courses, featured]);

  return (
    <div className="min-h-screen bg-[#090806] text-[#F6EAD0]" data-testid="academia-public-page">
      <PublicNav />
      <main>
        <section className="relative overflow-hidden border-b border-[#b38b42]/20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(209,162,78,.20),transparent_35%),radial-gradient(circle_at_80%_25%,rgba(86,72,42,.18),transparent_30%),linear-gradient(180deg,#120f0a,#090806)]" />
          <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-14 md:px-8 lg:grid-cols-[1.15fr_.85fr] lg:py-20">
            <div className="self-center">
              <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-[.38em] text-[#d8b36a]">
                <Sparkles size={14} /> Academia Khaos Delta 9
              </div>
              <h1 className="max-w-4xl font-cinzel text-4xl font-semibold leading-tight text-[#fff4d6] md:text-6xl">
                El Sendero del Conocimiento
              </h1>
              <p className="mt-5 max-w-2xl font-cormorant text-xl leading-relaxed text-[#c9bea6] md:text-2xl">
                Cursos, talleres, pruebas, revisiones y materiales organizados como un recorrido vivo. Cada avance abre un nuevo territorio académico.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/login" className="inline-flex min-h-12 items-center gap-2 rounded-2xl bg-[#d4ab5e] px-6 font-mont text-[11px] font-bold uppercase tracking-[.16em] text-[#171008]">
                  Entrar a la Academia <ArrowRight size={15} />
                </Link>
                <Link to="/register" className="inline-flex min-h-12 items-center gap-2 rounded-2xl border border-[#d4ab5e]/45 px-6 font-mont text-[11px] uppercase tracking-[.16em] text-[#f4d99a]">
                  Crear acceso
                </Link>
              </div>
            </div>

            <div className="rounded-[30px] border border-[#d4ab5e]/25 bg-black/25 p-5 shadow-2xl backdrop-blur">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="font-mont text-[9px] uppercase tracking-[.32em] text-[#bca77d]">Mapa académico</p>
                  <h2 className="mt-1 font-cinzel text-2xl text-[#f3ddb1]">Tu sendero</h2>
                </div>
                <Map className="text-[#d4ab5e]" />
              </div>
              <div className="space-y-2">
                {[
                  ["Umbral", "Explora cursos y talleres", Compass],
                  ["Módulos", "Avanza por territorios de conocimiento", BookOpen],
                  ["Talleres", "Entrega, revisión y retroalimentación", GraduationCap],
                  ["Biblioteca", "Recursos protegidos de tus cursos", Library],
                  ["Dominio", "Evaluación, progreso y certificación", Star],
                ].map(([title, desc, Icon], index) => (
                  <div key={title} className="flex items-center gap-4 rounded-2xl border border-white/7 bg-white/[.025] p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#d4ab5e]/30 bg-[#d4ab5e]/10 text-[#e4c37d]">
                      <Icon size={17} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-cinzel text-base text-[#f3ddb1]">{index + 1}. {title}</div>
                      <div className="mt-1 font-mont text-[10px] text-[#aaa08d]">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {featured && (
          <section className="mx-auto max-w-7xl px-5 py-10 md:px-8">
            <div className="overflow-hidden rounded-[32px] border border-[#d4ab5e]/25 bg-[#100d09] lg:grid lg:grid-cols-[1.1fr_.9fr]">
              <div className="relative min-h-[300px]">
                {featured.cover_url ? (
                  <img src={featured.cover_url} alt={featured.title} className="absolute inset-0 h-full w-full object-cover opacity-80" />
                ) : (
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,171,94,.22),transparent_58%),#0d0b08]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-black/20 to-[#100d09] lg:block hidden" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#100d09] via-transparent to-transparent lg:hidden" />
                <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-[#d4ab5e]/40 bg-black/55 px-4 py-2 font-mont text-[9px] uppercase tracking-[.2em] text-[#f1d79f]">
                  <Star size={12} /> Sendero destacado
                </span>
              </div>
              <div className="p-6 md:p-9">
                <div className="font-mont text-[9px] uppercase tracking-[.28em] text-[#bda97d]">{featured.level || "Nivel KD9"}</div>
                <h2 className="mt-3 font-cinzel text-3xl text-[#f5dfb0]">{featured.title}</h2>
                <p className="mt-4 font-cormorant text-xl leading-relaxed text-[#c5b9a3]">{featured.description}</p>
                <div className="mt-5 flex flex-wrap gap-4 font-mont text-[10px] text-[#aaa08d]">
                  <span><Clock3 size={13} className="mr-1 inline" /> {featured.duration || "A tu ritmo"}</span>
                  <span><BookOpen size={13} className="mr-1 inline" /> {featured.lessons_count} lecciones</span>
                  <span><Map size={13} className="mr-1 inline" /> {featured.modules_count} módulos</span>
                </div>
                <div className="mt-6 flex items-end justify-between gap-4">
                  <div>
                    <div className="font-mont text-[9px] uppercase tracking-[.2em] text-[#8d8371]">Costo de acceso</div>
                    <div className="mt-1 font-cinzel text-2xl text-[#f1cf8d]">
                      {featured.energy_cost > 0 ? `⚡ ${featured.energy_cost} KD` : "Acceso gratuito"}
                    </div>
                  </div>
                  <Link to="/login" className="rounded-xl border border-[#d4ab5e]/40 px-5 py-3 font-mont text-[10px] uppercase tracking-[.16em] text-[#f1d79f]">
                    Ver sendero
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="mx-auto max-w-7xl px-5 pb-16 md:px-8">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-mont text-[9px] uppercase tracking-[.3em] text-[#9f9072]">Explorar conocimiento</div>
              <h2 className="mt-2 font-cinzel text-3xl text-[#f0dfbb]">Cursos y talleres publicados</h2>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d4ab5e]/20 px-4 py-2 font-mont text-[9px] uppercase tracking-[.16em] text-[#c9b27e]">
              <Crown size={13} /> Premium: acceso académico total
            </div>
          </div>

          {loading && <div className="rounded-3xl border border-white/10 bg-white/[.025] p-8 font-mont text-sm text-[#aaa08d]">Cargando senderos…</div>}
          {!loading && courses.length === 0 && <div className="rounded-3xl border border-white/10 bg-white/[.025] p-10 font-cormorant text-xl text-[#aaa08d]">Próximamente publicaremos nuevos territorios de conocimiento.</div>}

          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {rest.map((course) => (
              <article key={course.slug} className="group overflow-hidden rounded-[26px] border border-white/10 bg-[#100d09] transition hover:-translate-y-1 hover:border-[#d4ab5e]/35">
                <div className="relative h-44 overflow-hidden">
                  {course.cover_url ? <img src={course.cover_url} alt={course.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" /> : <div className="h-full w-full bg-[radial-gradient(circle_at_center,rgba(212,171,94,.18),transparent_60%),#0b0907]" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#100d09] via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/55 px-3 py-1.5 font-mont text-[8px] uppercase tracking-[.16em] text-[#e7d1a6]">{course.level}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-cinzel text-xl text-[#f0ddb5]">{course.title}</h3>
                  <p className="mt-2 line-clamp-3 font-cormorant text-lg text-[#b7ac99]">{course.description}</p>
                  <div className="mt-4 flex gap-4 font-mont text-[9px] text-[#8f8575]">
                    <span>{course.modules_count} módulos</span>
                    <span>{course.lessons_count} lecciones</span>
                  </div>
                  <div className="mt-5 flex items-center justify-between border-t border-white/8 pt-4">
                    <span className="font-cinzel text-lg text-[#e8c781]">{course.energy_cost > 0 ? `⚡ ${course.energy_cost} KD` : "Gratis"}</span>
                    <Link to="/login" className="inline-flex items-center gap-1 font-mont text-[9px] uppercase tracking-[.14em] text-[#d9bd83]">Ingresar <ArrowRight size={12} /></Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AcademiaPublic;
